import {
  Button,
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Guid } from "guid-typescript";
import OrganizationalMetricFormDialog from "../components/metrics/OrganizationalMetricFormDialog";
import MetricBandsDialog from "../components/metrics/MetricBandsDialog";
import {
  getCompanies,
  getCompanyDepartments,
  getMetricDefinitions,
  getOrganizationalMetrics,
} from "../repository/data.repository";
import {
  MAX_PAGE_SIZE,
  MetricDefinition,
  MetricType,
  OrganizationalMetricEntry,
} from "../repository/data.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

const METRIC_TYPES: MetricType[] = [
  "Absenteeism",
  "Turnover",
  "Grievance",
  "Accident",
];

interface Option {
  readonly label: string;
  readonly value: string;
}

function MetricsEntriesTab() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const [companies, setCompanies] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [companyUid, setCompanyUid] = useState<string>();
  const [departmentUid, setDepartmentUid] = useState<string>();
  const [metricType, setMetricType] = useState<MetricType>();
  const [rows, setRows] = useState<OrganizationalMetricEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [trigger, setTrigger] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "update" | "delete">("add");
  const [dialogValues, setDialogValues] = useState<OrganizationalMetricEntry>();

  useEffect(() => {
    getCompanies({ searchQuery: { conditions: [] }, pageNumber: 0, pageSize: 200 })
      .then(({ data }) =>
        setCompanies(data.map((c) => ({ label: c.name, value: c.uid }))),
      )
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    setDepartments([]);
    setDepartmentUid(undefined);
    if (!companyUid) return;
    getCompanyDepartments({
      searchQuery: {
        conditions: [{ field: "companyUid", operation: 0 as any, values: [companyUid] }],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then(({ data }) =>
        setDepartments(data.map((d) => ({ label: d.name, value: d.uid }))),
      )
      .catch(() => setDepartments([]));
  }, [companyUid]);

  useEffect(() => {
    if (!companyUid) {
      setRows([]);
      return;
    }
    const conditions: any[] = [
      { field: "companyUid", operation: 0, values: [companyUid] },
    ];
    if (departmentUid)
      conditions.push({ field: "departmentUid", operation: 0, values: [departmentUid] });
    if (metricType)
      conditions.push({ field: "metricType", operation: 0, values: [metricType] });

    setLoading(true);
    getOrganizationalMetrics({
      searchQuery: { conditions },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [companyUid, departmentUid, metricType, trigger]);

  function openDialog(
    mode: "add" | "update" | "delete",
    record?: OrganizationalMetricEntry,
  ): void {
    setDialogMode(mode);
    setDialogValues(record);
    setDialogOpen(true);
  }

  const canEdit = permissions.can("Metrics", "edit");

  return (
    <Space direction="vertical" size="middle" className="flex w-full">
      <Card size="small">
        <Space wrap size="middle">
          <Select
            className="w-56"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Company"
            value={companyUid}
            options={companies}
            onChange={(v) => setCompanyUid(v)}
          />
          <Select
            className="w-48"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Department"
            disabled={!companyUid}
            value={departmentUid}
            options={departments}
            onChange={(v) => setDepartmentUid(v)}
          />
          <Select
            className="w-44"
            allowClear
            placeholder="Metric"
            value={metricType}
            options={METRIC_TYPES.map((t) => ({ label: t, value: t }))}
            onChange={(v) => setMetricType(v)}
          />
          <Button
            type="primary"
            disabled={!canEdit}
            onClick={() => openDialog("add")}
          >
            Add Entry
          </Button>
        </Space>
      </Card>

      {!companyUid ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a company to view its metric entries"
        />
      ) : (
        <Table
          size="small"
          rowKey="uid"
          loading={loading}
          dataSource={rows}
          pagination={false}
          columns={[
            { title: "Department", dataIndex: "departmentName", key: "departmentName" },
            { title: "Period", dataIndex: "companyFiscalPeriodName", key: "period" },
            { title: "Metric", dataIndex: "metricType", key: "metricType", width: 120 },
            {
              title: "Rate %",
              dataIndex: "rate",
              key: "rate",
              width: 100,
              render: (v: number) => (v != null ? v.toFixed(2) : "—"),
            },
            { title: "Headcount", dataIndex: "headcount", key: "headcount", width: 100 },
            { title: "Events", dataIndex: "eventCount", key: "eventCount", width: 90 },
            {
              title: "",
              key: "actions",
              width: 130,
              render: (_v, record: OrganizationalMetricEntry) => (
                <Space>
                  <Button
                    size="small"
                    type="link"
                    disabled={!canEdit}
                    onClick={() => openDialog("update", record)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    danger
                    disabled={!permissions.can("Metrics", "delete")}
                    onClick={() => openDialog("delete", record)}
                  >
                    Delete
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      <OrganizationalMetricFormDialog
        key={dialogMode + (dialogValues?.uid ?? "")}
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogValues}
        onFinish={() => {
          setDialogOpen(false);
          setTrigger(Guid.create().toString());
        }}
        onCancel={() => setDialogOpen(false)}
      />
    </Space>
  );
}

function MetricDefinitionsTab() {
  const [defs, setDefs] = useState<MetricDefinition[]>([]);
  const [bandsDialogDef, setBandsDialogDef] = useState<MetricDefinition>();

  function load(): void {
    getMetricDefinitions({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then(({ data }) => setDefs(data))
      .catch(() => setDefs([]));
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <Space direction="vertical" size="middle" className="flex w-full">
      {defs.map((def) => (
        <Card
          key={def.uid}
          size="small"
          title={
            <Space>
              <span>{def.name}</span>
              <Typography.Text type="secondary">
                base weight {def.baseWeight}
              </Typography.Text>
            </Space>
          }
          extra={
            <Button size="small" onClick={() => setBandsDialogDef(def)}>
              Manage bands
            </Button>
          }
        >
          <Table
            size="small"
            rowKey="uid"
            pagination={false}
            dataSource={[...def.bands].sort(
              (a, b) => a.upperThreshold - b.upperThreshold,
            )}
            columns={[
              {
                title: "Band",
                dataIndex: "label",
                key: "label",
                render: (label: string, r: any) => (
                  <Tag color={r.color}>{label}</Tag>
                ),
              },
              {
                title: "≤ Rate %",
                dataIndex: "upperThreshold",
                key: "upperThreshold",
                width: 140,
                render: (v: number) => (v >= 100000 ? "∞" : v),
              },
              {
                title: "Readiness multiplier",
                dataIndex: "readinessMultiplier",
                key: "readinessMultiplier",
                width: 180,
              },
            ]}
          />
        </Card>
      ))}
      {!defs.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No metric definitions" />
      )}
      <MetricBandsDialog
        open={!!bandsDialogDef}
        definition={bandsDialogDef}
        onFinish={() => {
          setBandsDialogDef(undefined);
          load();
        }}
        onCancel={() => setBandsDialogDef(undefined)}
      />
    </Space>
  );
}

function MetricsPage() {
  const items = useMemo(
    () => [
      { key: "entries", label: "Entries", children: <MetricsEntriesTab /> },
      {
        key: "definitions",
        label: "Definitions & Bands",
        children: <MetricDefinitionsTab />,
      },
    ],
    [],
  );

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Organizational Metrics
        </Typography.Title>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <Tabs items={items} />
      </Col>
    </Col>
  );
}

export default MetricsPage;
